// Parse the new-format predictions JSON (DynamicSampling output).
//
// On-disk shape (per side):
//   {
//     "metadata": {
//       "image_size": [H, W],
//       "number_of_frames": int,
//       "fps": float,
//       "support_keypoints": [name, ...],
//       "frame_image_extension": "jpg"
//     },
//     "frames": {
//       "000054": {
//         "left_wrist": { "pixel_coords": [x, y], "confidence_score": float, "status": null | "manual_annotated" | "occluded" | "out_of_frame" },
//         ...
//       },
//       ...
//     }
//   }
//
// We:
//   • Filter kpts to KEYPOINT_NAMES (single source of truth in
//     `config/keypoints.ts`) so adding/removing a joint there is the only
//     change needed.
//   • Build a FrameMap keyed by integer frame index that the rest of the
//     app (overlay, edits slice) consumes unchanged.
//   • Preserve the raw object so `save()` can re-emit untouched frames
//     byte-identical to the input.

import { CONFIDENCE_THRESHOLD } from '../../config/constants';
import { KEYPOINTS } from '../../config/keypoints';
import { t } from '../../config/strings';
import type { VideoMeta } from '../../store/video';
import type { FrameMap, KeypointRecord, NewKeypointRaw, NewPredictionsFile } from '../../types';

export interface ParsedNewPredictions {
  raw: NewPredictionsFile;
  frameMap: FrameMap;
  meta: VideoMeta;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isXY(v: unknown): v is [number, number] {
  return Array.isArray(v) && v.length === 2 && isFiniteNumber(v[0]) && isFiniteNumber(v[1]);
}

// Pixel [x, y] lies within the image. `w`/`h` are the image width/height.
function isInFrame(xy: [number, number], w: number, h: number): boolean {
  return xy[0] >= 0 && xy[0] < w && xy[1] >= 0 && xy[1] < h;
}

const OUT_OF_FRAME_XY: [number, number] = [-1, -1];

function readMeta(raw: NewPredictionsFile): VideoMeta {
  const m = raw.metadata;
  if (!m || typeof m !== 'object') {
    throw new Error(t.errors.missingMetadata);
  }
  if (!isFiniteNumber(m.fps) || m.fps <= 0) {
    throw new Error(t.errors.metadataFpsInvalid);
  }
  if (!isFiniteNumber(m.number_of_frames) || m.number_of_frames <= 0) {
    throw new Error(t.errors.metadataNumberOfFramesInvalid);
  }
  if (
    !Array.isArray(m.image_size) ||
    m.image_size.length !== 2 ||
    !isFiniteNumber(m.image_size[0]) ||
    !isFiniteNumber(m.image_size[1])
  ) {
    throw new Error(t.errors.metadataImageSizeMalformed);
  }
  return {
    fps: m.fps,
    totalFrames: m.number_of_frames,
    imageSize: [m.image_size[0], m.image_size[1]],
  };
}

export async function parseNewPredictionsBlob(blob: Blob): Promise<ParsedNewPredictions> {
  const text = await blob.text();
  let raw: NewPredictionsFile;
  try {
    raw = JSON.parse(text) as NewPredictionsFile;
  } catch (e) {
    throw new Error(t.errors.predictionsNotValidJson((e as Error).message), { cause: e });
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error(t.errors.predictionsRootNotObject);
  }
  const meta = readMeta(raw);
  if (!raw.frames || typeof raw.frames !== 'object') {
    throw new Error(t.errors.missingFramesObject);
  }

  // image_size is [H, W]; out-of-frame test needs width then height.
  const [imgH, imgW] = meta.imageSize;

  const frameMap: FrameMap = {};
  for (const [frameKey, frameRaw] of Object.entries(raw.frames)) {
    const frameIdx = Number(frameKey);
    if (!Number.isFinite(frameIdx) || !Number.isInteger(frameIdx) || frameIdx < 0) {
      continue; // skip malformed frame keys
    }
    if (!frameRaw || typeof frameRaw !== 'object') continue;
    // Dense: one record per configured keypoint, regardless of what the file
    // carries. Keypoints missing from the file, with null/invalid coords, or
    // with coords outside the image default to [-1, -1] / out_of_frame so the
    // roster always shows every configured joint (the side panel / overlay
    // honor `defaultVisibility` when no manual edit exists).
    const records: KeypointRecord[] = [];
    for (const { name } of KEYPOINTS) {
      const k = (frameRaw as Record<string, unknown>)[name] as NewKeypointRaw | undefined;
      if (k && isXY(k.pixel_coords) && isFiniteNumber(k.confidence_score)) {
        if (isInFrame(k.pixel_coords, imgW, imgH)) {
          records.push({
            name,
            xy: [k.pixel_coords[0], k.pixel_coords[1]],
            score: k.confidence_score,
            qualified: k.confidence_score >= CONFIDENCE_THRESHOLD,
            defaultVisibility: 'visible',
          });
          continue;
        }
        // In-file but out of frame: keep score for display, force out_of_frame.
        records.push({
          name,
          xy: [...OUT_OF_FRAME_XY],
          score: k.confidence_score,
          qualified: false,
          defaultVisibility: 'out_of_frame',
        });
        continue;
      }
      // Missing from file or null/invalid coords/score.
      records.push({
        name,
        xy: [...OUT_OF_FRAME_XY],
        score: 0,
        qualified: false,
        defaultVisibility: 'out_of_frame',
      });
    }
    frameMap[frameIdx] = records;
  }

  return { raw, frameMap, meta };
}
